import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  stripSearchParams,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import type { PredictionsScope } from '@/modules/query-keys';
import SpinnerThreeDots from '@/shared/ui/SpinnerThreeDots';
import Providers from './Providers';
import App from './App';
import DefaultLayout from './DefaultLayout';
import ProtectedRoute from './ProtectedRoute';
import RouteError from './RouteError';
import { searchSerialization } from './searchSerialization';

const rootRoute = createRootRoute({ component: Root });

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/Landing')),
});

// Public, chrome-less: the SSO redirect returns here logged-out.
export const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/callback',
  component: lazyRouteComponent(() => import('./pages/AuthCallback')),
});

// Public, chrome-less: the demo-stand entry the SSO operator page opens —
// local teardown, then the SSO redirect. `?to=arena` is the staker button's
// landing page.
export const demoResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'demo-reset',
  validateSearch: (
    raw: { to?: 'arena' } & SearchSchemaInput,
  ): { to?: 'arena' } => (raw.to === 'arena' ? { to: 'arena' } : {}),
  component: lazyRouteComponent(() => import('./pages/DemoReset')),
});

// Public, chrome-less: the legal documents' version indexes. The GDPR modal
// links into them and renders over every DefaultLayout page until the
// profile accepts, so under that chrome it would cover the page it opened.
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'terms',
  component: lazyRouteComponent(() => import('./pages/Legal/Terms')),
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'privacy',
  component: lazyRouteComponent(() => import('./pages/Legal/Privacy')),
});

const rulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'rules',
  component: lazyRouteComponent(() => import('./pages/Legal/Rules')),
});

// DefaultLayout hosts the public routes — the shareable market page (its
// chrome renders logged-out; the segment carries either a short share ref
// or a legacy full id) and a KOL's profile, which a market page links to
// and whose data is public too — plus the login-gated app pages.
const defaultLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'default',
  component: DefaultLayout,
});

export const marketRoute = createRoute({
  getParentRoute: () => defaultLayoutRoute,
  path: 'markets/$id',
  component: lazyRouteComponent(() => import('./pages/Market')),
});

// `section` is the page section and `tab` the history sub-tab: one param
// cannot carry both without the two clobbering each other's links.
export const kolProfileRoute = createRoute({
  getParentRoute: () => defaultLayoutRoute,
  path: 'kol/$handle',
  validateSearch: (
    raw: {
      tab?: 'active' | 'closed';
      section?: 'overview' | 'dna';
    } & SearchSchemaInput,
  ): { tab: 'active' | 'closed'; section: 'overview' | 'dna' } => ({
    tab: raw.tab === 'closed' ? 'closed' : 'active',
    section: raw.section === 'dna' ? 'dna' : 'overview',
  }),
  search: {
    middlewares: [stripSearchParams({ tab: 'active', section: 'overview' })],
  },
  component: lazyRouteComponent(() => import('./pages/Kols/KolProfile')),
});

const protectedRoute = createRoute({
  getParentRoute: () => defaultLayoutRoute,
  id: 'protected',
  component: ProtectedRoute,
});

const marketsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'markets',
  component: lazyRouteComponent(() => import('./pages/Markets')),
});

const arenaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'arena',
  component: lazyRouteComponent(() => import('./pages/Arena')),
});

const communityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'community',
  component: lazyRouteComponent(() => import('./pages/Community')),
});

// The page lives in the URL so a reload keeps it.
export const kolsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'kols',
  validateSearch: (
    raw: { page?: number } & SearchSchemaInput,
  ): { page: number } => {
    const page = Number(raw.page);
    return { page: Number.isInteger(page) && page > 0 ? page : 1 };
  },
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  component: lazyRouteComponent(() => import('./pages/Kols')),
});

const helpRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'help',
  component: lazyRouteComponent(() => import('./pages/HelpCenter')),
});

export const userRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'user/$username',
  component: lazyRouteComponent(() => import('./pages/User')),
});

export const userOverviewRoute = createRoute({
  getParentRoute: () => userRoute,
  path: '/',
  validateSearch: (
    raw: { tab?: PredictionsScope } & SearchSchemaInput,
  ): { tab: PredictionsScope } => ({
    tab: raw.tab === 'closed' || raw.tab === 'orders' ? raw.tab : 'active',
  }),
  search: { middlewares: [stripSearchParams({ tab: 'active' })] },
  component: lazyRouteComponent(
    () => import('./pages/User/Profile/OverviewTab'),
  ),
});

const userStrategiesRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'strategies',
  component: lazyRouteComponent(
    () => import('./pages/User/Profile/Strategies'),
  ),
});

export const userStakesRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'stakes/{-$view}',
  beforeLoad: ({ params }) => {
    if (
      params.view !== undefined &&
      params.view !== 'backers' &&
      params.view !== 'portfolio'
    ) {
      throw redirect({
        to: '/user/$username',
        params: { username: params.username },
        replace: true,
      });
    }
  },
  component: lazyRouteComponent(() => import('./pages/User/Profile/Stakes')),
});

const userWatchlistRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'watchlist',
  component: lazyRouteComponent(() => import('./pages/User/Profile/Watchlist')),
});

// Whether the tab may show is a React concern (the testers flag is a hook),
// so Profile gates it around its outlet.
const userInvitesRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'invites',
  component: lazyRouteComponent(() => import('./pages/User/Profile/Invites')),
});

// Any other segment under a profile lands on its overview.
const userUnknownTabRoute = createRoute({
  getParentRoute: () => userRoute,
  path: '$',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/user/$username',
      params: { username: params.username },
      replace: true,
    });
  },
});

export const wildcardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '$',
  component: lazyRouteComponent(() => import('./pages/Wildcard')),
});

export const routeTree = rootRoute.addChildren([
  landingRoute,
  authCallbackRoute,
  demoResetRoute,
  termsRoute,
  privacyRoute,
  rulesRoute,
  defaultLayoutRoute.addChildren([
    marketRoute,
    kolProfileRoute,
    protectedRoute.addChildren([
      marketsRoute,
      arenaRoute,
      communityRoute,
      kolsRoute,
      helpRoute,
      userRoute.addChildren([
        userOverviewRoute,
        userStrategiesRoute,
        userStakesRoute,
        userWatchlistRoute,
        userInvitesRoute,
        userUnknownTabRoute,
      ]),
      wildcardRoute,
    ]),
  ]),
]);

// Every page is its own chunk: a link fetches its target on hover, and a
// chunk still loading after the router's default second shows the spinner.
export const router = createRouter({
  routeTree,
  ...searchSerialization,
  defaultPreload: 'intent',
  defaultPendingComponent: PagePending,
  defaultErrorComponent: RouteError,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function Root() {
  return (
    <Providers>
      <App />
    </Providers>
  );
}

function PagePending() {
  return (
    <div className="mt-xl flex justify-center">
      <SpinnerThreeDots color="var(--gray)" size={10} />
    </div>
  );
}
